import { useState, useCallback, useEffect } from 'react';
import useLinkedStudiesApi from '../../api/requests/useLinkedStudiesApi';

/**
 * useLinkedStudies Hook
 *
 * Feature hook for fetching linked studies for an existing request.
 * Used in request detail views (customer progress, admin review) to display
 * the studies that were linked when the request was created.
 *
 * @param {number} requestId - The request ID to fetch linked studies for
 * @returns {Object} - { linkedStudies, linkedStudiesEnabled, loading, error }
 */
const useLinkedStudies = (requestId) => {
  const { getLinkedStudies } = useLinkedStudiesApi();
  const [linkedStudies, setLinkedStudies] = useState([]);
  const [linkedStudiesEnabled, setLinkedStudiesEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchLinkedStudies = useCallback(async () => {
    if (!requestId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await getLinkedStudies(requestId);
      setLinkedStudies(data.linkedStudies || []);
      setLinkedStudiesEnabled(data.linkedStudiesEnabled || false);
    } catch (err) {
      console.error('Error fetching linked studies:', err);
      setError(err.message || 'Failed to fetch linked studies');
      setLinkedStudies([]);
    } finally {
      setLoading(false);
    }
  }, [requestId, getLinkedStudies]);

  useEffect(() => {
    fetchLinkedStudies();
  }, [fetchLinkedStudies]);

  return {
    linkedStudies,
    linkedStudiesEnabled,
    loading,
    error,
    refetch: fetchLinkedStudies,
  };
};

export default useLinkedStudies;
